import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject } from 'rxjs';
import { Logger } from '../../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../../infrastructure/context/user-context-provider';
import { formatCurrencyString } from '../../../../utilities/CurrencyUtility';
import { PriceRangeModalComponent } from '../../../price-rates/components/price-range-modal/price-range-modal.component';
import { UpdateReservationPolicyRow, UpdateReservationPolicyColumns, UpdateRemovePropertyPolicyColumns } from '../../models/Policy';
import { PolicyService } from '../../services/policy.service';
import { SharedPolicyTableComponent } from '../shared-policy-table/shared-policy-table/shared-policy-table.component';

@Component({
  selector: 'app-update-policy-tool-dialog',
  templateUrl: './update-policy-tool-dialog.component.html',
  styleUrls: ['./update-policy-tool-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCheckbox,
    MatIcon,
    MatButtonModule,
    SharedPolicyTableComponent
  ]
})
export class UpdatePolicyToolDialogComponent implements OnInit {

  loading = new BehaviorSubject<boolean>(false);
  dataSource = new MatTableDataSource<UpdateReservationPolicyRow>();
  policiesToRemoveDataSource = new MatTableDataSource<UpdateReservationPolicyRow>();
  columns = UpdateReservationPolicyColumns;
  removePolicyColumns = UpdateRemovePropertyPolicyColumns;
  availablePolicies: any;
  updatePropertyPolicy: boolean = false;
  setSelectedPolicyAsPrimaryBoolean: boolean = false;
  shouldShowRemovePolicyButton: boolean = false;
  sharedPropertyPolicies: any;
  propertiesToUpdate: any;
  propertyHasPrimaryPolicy: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<UpdatePolicyToolDialogComponent>,
    private policyService: PolicyService,
    private logService: Logger,
    private userContextProvider: UserContextProvider,
    private priceRangeDialog: MatDialog
  ) { }

  ngOnInit() {
    if (this.data.component === 'properties') {
      this.updatePropertyPolicy = true;
    } else {
      this.updatePropertyPolicy = false;
    }

    if (this.data.policies) {
      if (this.data.component === 'properties') {
        this.shouldShowRemovePolicyButton = this.data.showRemovePolicyButton;
        this.sharedPropertyPolicies = this.data.sharedPropertyPolicies;
        this.propertiesToUpdate = this.data.propertiesToUpdate;
        this.propertyHasPrimaryPolicy = this.data.propertyHasPrimaryPolicy;
        
        if (this.sharedPropertyPolicies.length > 0) {
          this.poplateRemovePolicyTable();
        }
      }

      this.availablePolicies = this.data.policies;
      this.populatePolicyTable();
    }
  }

  async populatePolicyTable() {
    try {
      this.loading.next(true);
      this.dataSource.data = [];

      const mappedPolicies = this.availablePolicies.map((r: UpdateReservationPolicyRow) => this.buildPolicyTableRow(r));
      this.updateTableDataSource(mappedPolicies);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Policies', 'Error while retrieving Policies');
    } finally {
      this.loading.next(false);
    }
  }

  async poplateRemovePolicyTable() {
    try {
      this.loading.next(true);
      this.policiesToRemoveDataSource.data = [];

      const uniquePoliciesById = this.sharedPropertyPolicies.reduce((acc: { has: (arg0: any) => any; set: (arg0: any, arg1: any) => void; }, current: { insurance_policy_id: any; }) => {
        if (!acc.has(current.insurance_policy_id)) {
          acc.set(current.insurance_policy_id, current);
        }
        return acc;
      }, new Map());

      let uniquePolicyArrayById: any = Array.from(uniquePoliciesById.values());
      const mappedPoliciesToRemove = uniquePolicyArrayById.map((r: UpdateReservationPolicyRow) => this.buildRemovePolicyTableRow(r));
      this.updateRemovePolicyTableDataSource(mappedPoliciesToRemove);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Policies', 'Error while retrieving Policies');
    } finally {
      this.loading.next(false);
    }
  }

  buildPolicyTableRow(policy: any): UpdateReservationPolicyRow {
    const priceModel = this.policyService.getPriceModelById(policy.policy_price.pricing_model_id);
    const currencyCode = this.userContextProvider.currencyCode;

    return {
      id: policy.policy.id,
      insurancePolicyId: policy.insurance_policy_id,
      policyPriceId: policy.policy_price_id,
      name: policy.policy.name,
      contentsCoverage: formatCurrencyString(String(policy.policy.damage_coverage), currencyCode),
      hoLiabilityCoverage: formatCurrencyString(String(policy.policy.liability_coverage), currencyCode),
      dwellingCoverage: formatCurrencyString(String(policy.policy.dwelling_coverage), currencyCode),
      priceRate: policy.policy_price.name,
      priceModel: priceModel,
      deductible: formatCurrencyString(String(policy.policy_price.deductible), currencyCode),
      price: priceModel === 'Tiered nights' ? 'View Night Ranges' : formatCurrencyString(String(policy.policy_price.price), currencyCode),
      extraNightPrice: formatCurrencyString(String(policy.policy_price.extra_night_price), currencyCode)
    }
  }

  buildRemovePolicyTableRow(policy: any): any {
    const currencyCode = this.userContextProvider.currencyCode;

    return {
      id: policy.policy.id,
      insurancePolicyId: policy.insurance_policy_id,
      policyPriceId: policy.policy_price_id,
      name: policy.policy.name,
      contentsCoverage: formatCurrencyString(String(policy.policy.damage_coverage), currencyCode),
      hoLiabilityCoverage: formatCurrencyString(String(policy.policy.liability_coverage), currencyCode),
      dwellingCoverage: formatCurrencyString(String(policy.policy.dwelling_coverage), currencyCode)
    }
  }

  updateTableDataSource(policies: UpdateReservationPolicyRow[]) {
    this.dataSource = new MatTableDataSource(policies);
  }

  updateRemovePolicyTableDataSource(policies: UpdateReservationPolicyRow[]) {
    this.policiesToRemoveDataSource = new MatTableDataSource(policies);
  }

  onSelectPolicyClick(row: any) {
    let selectedPolicyId = Number(row.id);

    if (this.data.component === 'properties') {
      let updatePropertyPolicyPayload = {
        selectedPolicyId: selectedPolicyId,
        setPolicyAsPrimary: this.setSelectedPolicyAsPrimaryBoolean
      }
      this.dialogRef.close(updatePropertyPolicyPayload);
    } else {
      this.dialogRef.close(selectedPolicyId);
    }
  }

  cancelUpdatePolicy() {
    this.dialogRef.close(0);
  }

  onViewRangesOpenClick(row: any) {
    this.priceRangeDialog.open(PriceRangeModalComponent, {
      width: '70%',
      data: row
    });
  }

  setSelectedPolicyAsPrimary(event: MatCheckboxChange) {
    this.setSelectedPolicyAsPrimaryBoolean = event.checked;
  }

  onRemovePolicyClick(row: any) {
    const sharedPoliciesToUpdate = this.sharedPropertyPolicies.filter((policy: any) => policy.insurance_policy_id === row.insurancePolicyId);
    this.removeSharedPropertyPolicy(sharedPoliciesToUpdate);
  }

  removeSharedPropertyPolicy(sharedPolicies: any) {
    let ctr = 0;
    sharedPolicies.forEach((policy: any) => {
      ctr++;
      let payload: any = {
        id: policy.id,
        is_primary: 0,
        is_active: 0,
        keep_history: 0
      }
      this.policyService.deletePolicyByComponent(policy.id, payload, 'properties').then(_ => {
        if (ctr === sharedPolicies.length) {
          this.dialogRef.close(0);
        }
      });
    });
  }
}